import React, { useEffect, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useQuery, useMutation } from '@apollo/client'
import { Formik, Form, Field, FieldProps } from 'formik'
import { Row, Col, Result } from 'antd'
import { MehOutlined } from '@ant-design/icons'
import Estimation from './Estimation'
import ComplementaryOptions from './ComplementaryOptions'
import Language from './Language'
import LegalFormalities from './LegalFormalities'
import { button } from 'styles/button'
import { DomiciliationType, EstimationType } from '../../../types'
import CentredLoader from 'components/CentredLoader'
import {
  openValidationPopup,
  openErrorPopup,
} from 'components/ConfirmationPopups'
import {
  ESTIMATIONS_QUERY,
  CREATE_ESTIMATIONS_MUTATION,
  SEND_ESTIMATIONS_MUTATION,
  QUOTATION_QUERY,
} from 'graphql/Estimation'

interface Props {
  domiciliation: DomiciliationType
  estimationVariables: any
  setEstimationVariables: any
  hubspotDropboxEmail?: string
}

interface IValues {
  complementaryOptionIds: Array<number>
  toAdmin: boolean
  language: string
}

interface CreateEstimationVars {
  domiciliationId: number
  addressId: number
  legalStatusId: number
  estimations: Array<EstimationVars>
}

interface EstimationVars {
  paymentType: string
  promoCodeId?: number
}

interface EstimationCollection {
  monthly: EstimationElement
  yearly: EstimationElement
  twoYearly: EstimationElement
  threeYearly: EstimationElement
}

interface EstimationElement {
  checked: boolean
  persisted?: boolean
  estimation?: EstimationType
}

type PaymentType = 'monthly' | 'yearly' | 'two_yearly' | 'three_yearly'
type ElementType = 'monthly' | 'yearly' | 'twoYearly' | 'threeYearly'
const estimationElementTypes: Array<ElementType> = [
  'monthly',
  'yearly',
  'twoYearly',
  'threeYearly',
]
const mapElementTypeToPaymentType = (elementType: ElementType): PaymentType => {
  if (elementType === 'threeYearly') return 'three_yearly'
  if (elementType === 'twoYearly') return 'two_yearly'
  else return elementType
}

const reducer = (state: EstimationCollection, action: any) => {
  switch (action.type) {
    case 'monthly':
      return { ...state, monthly: action.value }
    case 'yearly':
      return { ...state, yearly: action.value }
    case 'two_yearly':
      return { ...state, twoYearly: action.value }
    case 'three_yearly':
      return { ...state, threeYearly: action.value }
    default:
      throw new Error()
  }
}

const Estimations = (props: Props): JSX.Element => {
  const { t } = useTranslation()

  const [estimations, dispatchEstimation] = useReducer(reducer, {
    monthly: { checked: props.domiciliation.paymentType === 'monthly' },
    yearly: { checked: props.domiciliation.paymentType === 'yearly' },
    twoYearly: { checked: props.domiciliation.paymentType === 'two_yearly' },
    threeYearly: {
      checked: props.domiciliation.paymentType === 'three_yearly',
    },
  })

  const { loading, data } = useQuery(ESTIMATIONS_QUERY, {
    fetchPolicy: 'no-cache',
    skip:
      !props.estimationVariables.addressId ||
      !props.estimationVariables.legalStatusId,
    variables: {
      domiciliationId: props.domiciliation.id,
      ...props.estimationVariables,
    },
  })

  const { loading: loadingQuotation, data: dataQuotation } = useQuery(
    QUOTATION_QUERY,
    { variables: { domiciliationId: props.domiciliation.id } }
  )

  useEffect(() => {
    if (data?.estimations && data.estimations.length > 0) {
      loadEstimations(data.estimations)
    }
  }, [data])

  const [sendEstimations, { loading: sendEstimationsLoading }] = useMutation(
    SEND_ESTIMATIONS_MUTATION
  )

  const [
    createEstimations,
    { loading: createEstimationsLoading, data: createEstimationsData },
  ] = useMutation(CREATE_ESTIMATIONS_MUTATION)

  useEffect(() => {
    if (createEstimationsData?.createEstimations?.estimations)
      loadEstimations(createEstimationsData.createEstimations.estimations)
  }, [createEstimationsData])

  const loadEstimations = (estimationDatas: Array<EstimationType>) => {
    estimationDatas.forEach((estimation: EstimationType) => {
      const estimationElement = {
        checked: true,
        persisted: true,
        estimation: estimation,
      }

      dispatchEstimation({
        type: estimation.paymentType,
        value: estimationElement,
      })
    })
  }

  const onChecked = (
    estimationElement: EstimationElement,
    paymentType: PaymentType
  ) => {
    estimationElement.checked = !estimationElement.checked
    dispatchEstimation({
      type: paymentType,
      value: estimationElement,
    })
  }

  const onEstimationChange = (estimation: EstimationType) => {
    const estimationElement: EstimationElement = {
      checked: true,
      persisted: false,
      estimation: estimation,
    }

    dispatchEstimation({
      type: estimation.paymentType,
      value: estimationElement,
    })
  }

  const estimationToParameter = (
    estimation: EstimationType
  ): EstimationVars => {
    return {
      paymentType: estimation.paymentType,
      promoCodeId: estimation.promoCode?.id,
    }
  }

  const onSubmitForm = (values: IValues) => {
    const parameters: CreateEstimationVars = {
      domiciliationId: props.domiciliation.id,
      estimations: [],
      ...props.estimationVariables,
      ...values,
    }
    estimationElementTypes.forEach(elementType => {
      const estimationElement: EstimationElement = estimations[elementType]

      if (
        estimationElement.checked &&
        !estimationElement.persisted &&
        estimationElement.estimation
      )
        parameters.estimations.push(
          estimationToParameter(estimationElement.estimation)
        )
    })

    tryCreateEstimation(parameters).then(({ data }) => {
      createDevis(data, values)
    })
  }

  const tryCreateEstimation = async (parameters: any) => {
    if (parameters.estimations.length > 0)
      return createEstimations({ variables: parameters })
    else {
      return { data: { createEstimations: { estimations: [] } } }
    }
  }

  const createDevis = async (data: any, values: IValues) => {
    const persistedEstimationIds = estimationElementTypes
      .map(elementType => {
        return estimations[elementType]
      })
      .map((estimationElement: EstimationElement) => {
        if (estimationElement.persisted && estimationElement.checked)
          return estimationElement.estimation?.id
      })
      .filter(x => x !== undefined && x !== null)

    const newEstimationIds = data.createEstimations.estimations.map(
      (estimation: EstimationType) => estimation.id
    )

    const ids = [...newEstimationIds, ...persistedEstimationIds]
    sendEstimations({
      variables: {
        domiciliationId: props.domiciliation.id,
        estimationIds: ids,
        hubspotDropboxEmail: props.hubspotDropboxEmail,
        ...values,
      },
    })
      .then(() =>
        openValidationPopup(
          t('sendEstimations.successTitle'),
          values.toAdmin
            ? t('sendEstimations.successAdminDescription')
            : t('sendEstimations.successProspectDescription')
        )
      )
      .catch(e => openErrorPopup(t('sendEstimations.errorTitle'), e.message))
  }

  if (loading || loadingQuotation) return <CentredLoader />

  const available =
    !!props.estimationVariables.addressId &&
    !!props.estimationVariables.legalStatusId &&
    !!props.estimationVariables.mailServiceId

  return (
    <Wrapper>
      <h4>Devis</h4>
      {available ? (
        <Formik
          initialValues={{
            complementaryOptionIds: [],
            toAdmin: false,
            language: 'French',
            withLegalFormality:
              sessionStorage.getItem('withLegalFormality') === 'true',
          }}
          onSubmit={onSubmitForm}
        >
          {helpers => {
            return (
              <Form>
                <Row gutter={[24, 48]}>
                  {estimationElementTypes.map(elementType => {
                    const estimationElement: EstimationElement =
                      estimations[elementType]
                    return (
                      <Col span={6} key={elementType}>
                        <Field
                          onChange={() =>
                            onChecked(
                              estimationElement,
                              mapElementTypeToPaymentType(elementType)
                            )
                          }
                          checked={estimationElement.checked}
                          type="checkbox"
                          name="partner"
                          value={elementType}
                        />
                        <label className="checkbox-label">
                          {t(elementType)}
                        </label>
                        {estimationElement.checked ? (
                          <Estimation
                            onChange={onEstimationChange}
                            paymentType={mapElementTypeToPaymentType(
                              elementType
                            )}
                            estimation={estimationElement.estimation}
                            domiciliation={props.domiciliation}
                            estimationVariables={{
                              ...props.estimationVariables,
                            }}
                          />
                        ) : null}
                      </Col>
                    )
                  })}
                </Row>
                <Row gutter={[0, 48]}>
                  <Field name="complementaryOptionIds">
                    {({ field, form }: FieldProps) => (
                      <ComplementaryOptions
                        field={field}
                        form={form}
                        defaultValues={
                          dataQuotation?.lastQuotation?.complementaryOptions ||
                          []
                        }
                      />
                    )}
                  </Field>
                </Row>
                <Row gutter={[0, 48]}>
                  <Field name="language">
                    {({ field, form }: FieldProps) => (
                      <Language field={field} form={form} />
                    )}
                  </Field>
                </Row>
                <Field name="withLegalFormality">
                  {({ field, form }: FieldProps) => (
                    <LegalFormalities
                      defaultValue={
                        !!dataQuotation?.lastQuotation?.legalFormality
                      }
                      onChange={(checked: boolean) => {
                        form.setFieldValue(field.name, checked)
                        props.setEstimationVariables({
                          ...props.estimationVariables,
                          withLegalFormality: checked,
                        })
                      }}
                    />
                  )}
                </Field>
                <Row justify="end">
                  <Col span={6}>
                    {sendEstimationsLoading || createEstimationsLoading ? (
                      <CentredLoader />
                    ) : (
                      <Row gutter={16}>
                        <Col className="gutter-row">
                          <button
                            type="submit"
                            onClick={() => {
                              helpers.setFieldValue('toAdmin', true)
                            }}
                          >
                            RECEVOIR LE DEVIS
                          </button>
                        </Col>
                        <Col className="gutter-row">
                          <button
                            type="submit"
                            onClick={() => {
                              helpers.setFieldValue('toAdmin', false)
                            }}
                          >
                            ENVOYER LE DEVIS
                          </button>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </Form>
            )
          }}
        </Formik>
      ) : (
        <Result
          icon={<MehOutlined style={{ fontSize: '90px', color: '#1576E1' }} />}
          title={t('estimations.notAvailable')}
          style={{ width: '70%', margin: 'auto' }}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin-left: 3%;
  margin-right: 3%;
  margin-top: 60px;

  .checkbox--group {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }

  .checkbox-content {
    flex-direction: row;
    width: 30%;
  }

  .checkbox-label {
    margin-left: 10px;
    vertical-align: middle;
  }

  ${button}
`

const ErrorDiv = styled.div`
  color: red;
`

export default Estimations
